import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField, createFilterOptions } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'shared/hooks';
import { useProductSearch } from 'shared/queries';
import { Brands, HTMLUListElementAttributes, ProductLookup } from 'shared/types';
import { discountRecommendationBrands } from '../constants';

interface ProductAutocompleteProps {
  name: `${string}` | `${string}.${string}` | `${string}.${number}`;
  defaultValue: string;
  onSelect: (product: string | Record<keyof ProductLookup, string> | null) => void;
  onClear: () => void;
  error?: boolean;
  helperText?: string;
}

export function ProductAutocomplete({
  name,
  onSelect,
  onClear,
  error,
  helperText,
  defaultValue = '',
}: ProductAutocompleteProps) {
  const [isFirstOnInputChangeCall, setIsFirstOnInputChangeCall] = useState<boolean>(true);
  const { watch } = useFormContext();
  const {
    field: { value, ref, onChange },
  } = useController({
    name,
    rules: { required: { message: 'Product Name is required', value: true } },
    defaultValue,
  });
  const { brand } = useParams<{ brand: Brands }>();
  const isDiscountRecommendationBrands = discountRecommendationBrands.includes(brand);
  const search = (watch(name) as unknown) as string;
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading, isFetching } = useProductSearch(debouncedSearch, brand);
  const loading = isLoading || isFetching;

  // This is what is used to do the front end filtering, but not necessarily for what
  // exactly is shown as the option text.
  const filter = createFilterOptions({
    matchFrom: isDiscountRecommendationBrands ? 'start' : 'any',
    stringify: (option: Record<keyof ProductLookup, string>) =>
      option.addDisplayLabel ||
      `${option.itemid} - ${option.extendeddesc || option.itemdescription}${
        isDiscountRecommendationBrands ? '' : ' - ' + option.vendorname
      } ${option.concatenateditem}`,
  });

  return (
    <Autocomplete
      id={name}
      inputValue={value}
      onChange={async (_, value, reason) => {
        onSelect(value);
        if (reason === 'clear') {
          onClear();
        }
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'clear') {
          onClear();
          return;
        }
        if (isFirstOnInputChangeCall && newValue === '') {
          setIsFirstOnInputChangeCall(false);
          return;
        } else if (isFirstOnInputChangeCall) {
          setIsFirstOnInputChangeCall(false);
        }
        onChange(newValue);
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);
        if (params.inputValue !== '') {
          filtered.push({
            itemdescription: '',
            itemid: '',
            concatenateditem: '',
            producttype: '',
            listprice: '',
            marginatlist: '',
            vendorid: '',
            vendorname: '',
            lastcostpaid: '',
            uniqueid: '',
            itemdescriptionadj: '',
            marginpercentileeighty: '',
            lasttransactiondate: '',
            costdate: '',
            outofscope: '',
            inputValue: params.inputValue,
            addDisplayLabel: `Add "${params.inputValue}"`,
            brand,
            quantitybreakflag: '',
            extendeddesc: '',
            costeffectivedate: '',
            listpriceeffectivedate: '',
            unitofmeasure: '',
            factorsheetname: '',
            mrovscapital: '',
            transactioncount: '',
            exchangerate: '',
            freightadder: '',
            currencytype: '',
          });
        }
        return filtered;
      }}
      freeSolo
      selectOnFocus
      clearOnBlur
      isOptionEqualToValue={(option, value) => option.itemid === value.itemid && option.vendorid === value.vendorid}
      getOptionLabel={option => option.inputValue || option.itemdescription}
      renderOption={(props, option) => (
        <li {...props} key={option.uniqueid}>
          {option.addDisplayLabel ||
            `${option.itemid} - ${option.extendeddesc || option.itemdescription}${
              isDiscountRecommendationBrands ? '' : ' - ' + option.vendorname
            }`}
        </li>
      )}
      options={data || []}
      loading={loading}
      fullWidth
      ref={ref}
      data-testid={`${name}-combobox`}
      ListboxProps={{ 'data-testid': `${name}-listbox` } as HTMLUListElementAttributes}
      renderInput={params => (
        <TextField
          {...params}
          label='Product'
          variant='outlined'
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress color='inherit' size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
